<template>
  <section :id="module.id" class="video-section"
    :class="[(isNestedModule || isSliderModule) ? '-nested' : '', gridSpanClass]" :style="[videoSectionElStyles]">
    <GlobalUIMedia v-if="module.config.general.showMedia" :media="module.config.general.media" :lazy="lazy" />
    <div class="video-wrapper" :style="[videoWrapperElStyles, modulePadding, nestedModulePadding]">
      <div v-if="!isVideoLoaded" class="thumbnail" :style="thumbnailElStyles">
        <GlobalUIMedia :media="module.config.general.thumbnail" :lazy="lazy" />
        <button class="play-button" :style="playButtonElStyles" aria-label="Play" @click="loadVideo">
          <IconPlay width="16" height="19" />
        </button>
      </div>

      <div v-else class="video-container">
        <video v-if="videoSource === 'upload' || videoSource === 'other'" :src="determineUploadOrOtherUrl" controls
          controlsList="nodownload" autoplay :style="videoElStyles" :data-test-id="videoSource" />
        <iframe v-else :src="determineEmbedUrl"
          allow="accelerometer; autoplay; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
          referrerpolicy="strict-origin-when-cross-origin" allowfullscreen :style="iFrameElStyles"
          :data-test-id="videoSource" />
      </div>
    </div>
  </section>
</template>

<script>
import videoModule from "./videoDefaultModuleProp.js";
import { globalComputed, globalMethods } from "../GlobalUI/mixins/global.js";
import getWhatchadoPath from "../../utils/getWhatchadoPath.ts";
import { VideoSource } from "../../utils/enums/VideoSource.ts";

import { colord } from "colord";
import getVideoId from "get-video-id";

import IconPlay from "../Icons/IconPlay.vue";
import { galleryChildrenComputed } from "../Gallery/GalleryChildrenMethods.ts";
import { GalleryGridSizes } from "../Gallery/GalleryGridSizes.ts";
import GlobalUIMedia from "../GlobalUI/GlobalUIMedia.vue";

export default {
  name: "Video",
  components: { IconPlay, GlobalUIMedia },
  mixins: [globalComputed, globalMethods, galleryChildrenComputed],
  props: {
    // when used in a gallery module, this is used for adjustments
    isNestedModule: {
      type: Boolean,
      default: false,
    },
    // when used in a slider module, this is used for adjustments
    isSliderModule: {
      type: Boolean,
      default: false,
    },
    // should only be filled when its a nested module
    parent: {
      type: Object,
      default() {
        return {};
      },
    },
    module: {
      type: Object,
      required: true,
      default: () => videoModule,
    },
    lazy: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isVideoLoaded: false,
    };
  },
  computed: {
    videoSource() {
      return this.module.config.general.video.source.value;
    },
    uploadedVideoUrl() {
      return this.module.config.general.video.uploadedVideo.media.url;
    },
    embeddedVideoUrl() {
      return this.module.config.general.video.embeddedVideoUrl;
    },
    determineEmbedUrl() {
      if (this.videoSource === VideoSource.YOUTUBE) {
        const videoId = getVideoId(this.embeddedVideoUrl).id;

        return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`;
      } else if (this.videoSource === VideoSource.VIMEO) {
        const videoId = getVideoId(this.embeddedVideoUrl).id;

        return `https://player.vimeo.com/video/${videoId}?autoplay=1`;
      } else if (this.videoSource === VideoSource.WHATCHADO) {
        const videoPath = getWhatchadoPath(this.embeddedVideoUrl);

        return `https://www.whatchado.com/de/embeds/videos/${videoPath}?autoplay=true`;
      }
    },
    determineUploadOrOtherUrl() {
      if (this.videoSource === VideoSource.UPLOAD) {
        return this.uploadedVideoUrl;
      } else if (this.videoSource === VideoSource.OTHER) {
        return this.embeddedVideoUrl;
      }
    },
    videoSectionElStyles() {
      const { backgroundColor, showMedia } = this.module.config.general;

      let borderRadius;
      if (this.isNestedModule || this.isSliderModule) {
        borderRadius =
          this.parent.config.general.corners.value === "round"
            ? "var(--cr-border-radius-large)"
            : "initial";
      }

      return {
        backgroundColor: !showMedia ? backgroundColor?.value : "",
        borderRadius,
      };
    },
    videoWrapperElStyles() {


      let minHeight;
      let maxWidth;
      if (!this.isSliderModule) {

        const { template } = this.module.config.general;

        maxWidth = template.component === "VideoBig" ? "1600px" : "950px";

        if (this.isNestedModule) {
          minHeight = template.component === "VideoBig" ? "600px" : "420px";

          if (
            template.component === "VideoSmall" &&
            this.module.config.general.blockWidth.value !== "full"
          ) {
            maxWidth = null;
          }
        }
      } else {
        maxWidth = null
        minHeight = null
      }

      return {
        minHeight,
        maxWidth,
      };
    },
    thumbnailElStyles() {
      let { corners } = this.module.config.general;

      if (this.isNestedModule || this.isSliderModule) {
        corners = this.parent.config.general.corners;
      }

      return {
        borderRadius:
          corners.value === "round"
            ? "var(--cr-border-radius-large)"
            : "initial",
      };
    },
    playButtonElStyles() {
      const { playButtonColor } = this.module.config.general;

      return {
        backgroundColor: playButtonColor.value,
        // Determine color of play symbol based on bg color
        color: colord(playButtonColor.value).isDark() ? "white" : "black",
      };
    },
    videoElStyles() {
      let { corners } = this.module.config.general;

      if (this.isNestedModule || this.isSliderModule) {
        corners = this.parent.config.general.corners;
      }

      return {
        borderRadius:
          corners.value === "round"
            ? "var(--cr-border-radius-large)"
            : "initial",
      };
    },
    iFrameElStyles() {
      let { corners } = this.module.config.general;

      if (this.isNestedModule || this.isSliderModule) {
        corners = this.parent.config.general.corners;
      }

      return {
        borderRadius:
          corners.value === "round"
            ? "var(--cr-border-radius-large)"
            : "initial",
      };
    },
  },
  methods: {
    loadVideo() {
      this.isVideoLoaded = true;
      this.$emit("emittedClick", this.module.config);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../Gallery/galleryChildren";
@include reset;

.video-section {
  container-type: inline-size;
  contain: layout;

  display: flex;
  align-items: center;
  justify-content: center;
}

.video-wrapper {
  width: 100%;
  max-width: 1600px;
  height: fit-content;
  margin-inline: var(--cr-spacing-l);

  @include tablet {
    margin-inline: var(--cr-spacing-m);
  }

  @include mobile {
    margin-inline: var(--cr-spacing-xs);
  }

  .-nested & {
    height: 100%;
    width: 100%;
    max-width: 100%;
    place-content: center;
    margin-inline: 0;
    overflow: hidden;

    @include tablet {
      margin-inline: 0;
    }

    @include mobile {
      margin-inline: 0;
    }
  }
}

.thumbnail {
  position: relative;

  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  aspect-ratio: 16/9;

  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;

  .-nested & {
    aspect-ratio: unset;
    height: 100%;
    width: 100%;
  }

  .ratio-SixteenToNine & {
    aspect-ratio: 16/9;
  }

  .ratio-OneToOne & {
    aspect-ratio: 1;
  }

  .ratio-NineToSixteen & {
    aspect-ratio: 9/16;
  }

  .ratio-FourToThree & {
    aspect-ratio: 4/3;
  }

  &::after {
    content: "";

    position: absolute;
    z-index: 0;
    inset: 0;

    opacity: 0;
    background-color: black;

    transition: opacity 200ms ease-in-out;
  }

  &:hover {
    .play-button {
      opacity: 1;
    }

    &::after {
      opacity: 0.25;
    }
  }
}

.play-button {
  cursor: pointer;

  z-index: 1;

  display: grid;
  place-content: center;

  width: 64px;
  height: 64px;

  opacity: 0.5;
  border: none;
  border-radius: 50%;

  transition: opacity 200ms ease-in-out;
}

.video-container {
  video {
    aspect-ratio: 16/9;
    width: 100%;
    height: 100%;

    .ratio-SixteenToNine & {
      aspect-ratio: 16/9;
    }

    .ratio-OneToOne & {
      aspect-ratio: 1;
    }

    .ratio-NineToSixteen & {
      aspect-ratio: 9/16;
    }

    .ratio-FourToThree & {
      aspect-ratio: 4/3;
    }
  }

  iframe {
    aspect-ratio: 16/9;
    width: 100%;
    height: 100%;
    border: none;

    .ratio-SixteenToNine & {
      aspect-ratio: 16/9;
    }

    .ratio-OneToOne & {
      aspect-ratio: 1;
    }

    .ratio-NineToSixteen & {
      aspect-ratio: 9/16;
    }

    .ratio-FourToThree & {
      aspect-ratio: 4/3;
    }
  }
}
</style>
